/**
 * Internationalisation (docs/22).
 *
 * Ressources importées statiquement : en phase 0 il n'y a qu'un namespace, le
 * découpage par feature avec chargement à la demande viendra avec les écrans.
 */
import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import accountEn from "@/locales/en/account.json";
import adminEn from "@/locales/en/admin.json";
import authEn from "@/locales/en/auth.json";
import billingEn from "@/locales/en/billing.json";
import catalogEn from "@/locales/en/catalog.json";
import commonEn from "@/locales/en/common.json";
import learningEn from "@/locales/en/learning.json";
import legalEn from "@/locales/en/legal.json";
import messagingEn from "@/locales/en/messaging.json";
import notificationsEn from "@/locales/en/notifications.json";
import storageEn from "@/locales/en/storage.json";
import accountFr from "@/locales/fr/account.json";
import adminFr from "@/locales/fr/admin.json";
import authFr from "@/locales/fr/auth.json";
import billingFr from "@/locales/fr/billing.json";
import catalogFr from "@/locales/fr/catalog.json";
import commonFr from "@/locales/fr/common.json";
import learningFr from "@/locales/fr/learning.json";
import legalFr from "@/locales/fr/legal.json";
import messagingFr from "@/locales/fr/messaging.json";
import notificationsFr from "@/locales/fr/notifications.json";
import storageFr from "@/locales/fr/storage.json";

export const DEFAULT_LOCALE = "fr";
export const SUPPORTED_LOCALES = ["fr", "en"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const resources = {
  fr: {
    common: commonFr,
    auth: authFr,
    admin: adminFr,
    account: accountFr,
    catalog: catalogFr,
    learning: learningFr,
    legal: legalFr,
    billing: billingFr,
    storage: storageFr,
    notifications: notificationsFr,
    messaging: messagingFr,
  },
  en: {
    common: commonEn,
    auth: authEn,
    admin: adminEn,
    account: accountEn,
    catalog: catalogEn,
    learning: learningEn,
    legal: legalEn,
    billing: billingEn,
    storage: storageEn,
    notifications: notificationsEn,
    messaging: messagingEn,
  },
} as const;

export const NAMESPACES = [
  "common",
  "auth",
  "admin",
  "account",
  "catalog",
  "learning",
  "legal",
  "billing",
  "storage",
  "notifications",
  "messaging",
] as const;

/**
 * Accorde `<html lang>` à la langue affichée — WCAG 3.1.1 (*Language of Page*).
 *
 * **Le défaut qu'elle corrige était invisible à l'œil et grossier à l'oreille** :
 * `index.html` porte `lang="fr"` en dur, et il y restait après un passage à
 * l'anglais. Un lecteur d'écran lit alors un texte anglais avec la synthèse
 * vocale française — « the course is published » prononcé à la française —, ce
 * qu'aucune relecture visuelle ne peut attraper. C'est aussi ce que lit un
 * correcteur orthographique, un traducteur automatique et un moteur de
 * recherche.
 *
 * Posée sur l'événement plutôt qu'appelée par le sélecteur de langue : le
 * changement vient aussi de l'URL (`?lang=en`), du `localStorage` et du
 * navigateur. Un seul point d'écoute couvre les quatre ; quatre appels en
 * oublieraient un.
 */
function syncDocumentLanguage(instance: typeof i18next): void {
  const apply = (language: string) => {
    // La langue seule, sans le pays : `fr-CA` et `fr` désignent la même synthèse
    // vocale, et la plateforme n'a de toute façon que deux traductions.
    document.documentElement.setAttribute("lang", language.split("-")[0] ?? DEFAULT_LOCALE);
  };
  apply(instance.language || DEFAULT_LOCALE);
  instance.on("languageChanged", apply);
}

export async function initI18n(locale?: string): Promise<typeof i18next> {
  if (i18next.isInitialized) {
    return i18next;
  }
  await i18next
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      // Ordre de priorité : préférence explicite → URL → navigateur (docs/22 §2).
      ...(locale ? { lng: locale } : {}),
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      defaultNS: "common",
      ns: [...NAMESPACES],
      interpolation: { escapeValue: false }, // React échappe déjà
      detection: {
        order: ["querystring", "localStorage", "navigator"],
        lookupQuerystring: "lang",
        lookupLocalStorage: "teach.locale",
        caches: ["localStorage"],
      },
      returnNull: false,
    });
  syncDocumentLanguage(i18next);
  return i18next;
}

export { default as i18n } from "i18next";
